import React from 'react';
import { Link } from 'react-router-dom';
import { PUBLIC_SITE_NAME, SIGNED_IN_PRODUCT_NAME, LOGO_URL, BG_LOGO_URL, BG_IMAGE_URL } from '../../config/brand';
import { BrandMark } from '../Brand';

export const MAGENTA = '#E93AF7';
export const TEAL_BTN = 'rgba(0,102,83,0.85)';

const Logo = () => (LOGO_URL
  ? <img src={LOGO_URL} alt={PUBLIC_SITE_NAME} className="h-[56px] md:h-[84px] w-auto object-contain" data-testid="brand-logo-image" />
  : <BrandMark size={84} />);

export const Banner = () => (
  <div className="pt-10 pb-8 md:pt-24 md:pb-14 px-4 md:px-[42px]" data-testid="onboarding-banner">
    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5 md:gap-8">
      <div className="flex items-center gap-3 md:gap-4">
        <Logo />
        <div className="leading-none" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          <div className="text-[30px] md:text-[36px] font-extrabold uppercase text-[#2EE6C3] drop-shadow-[0_2px_0_rgba(0,0,0,0.6)]">Cathie Wood</div>
          <div className="text-[24px] md:text-[28px] font-extrabold uppercase text-[#2EE6C3] drop-shadow-[0_2px_0_rgba(0,0,0,0.6)] mt-1">Capital</div>
        </div>
      </div>
      <div className="md:text-right text-white italic font-semibold text-[22px] md:text-[34px] leading-tight md:pr-40 md:pt-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
        Supercharge<br />Your Life<br />with Us!
      </div>
    </div>
    <div className="mt-6 md:mt-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      <div className="text-[18px] md:text-[28px] font-extrabold uppercase tracking-wide text-[#2EE6C3]">We are the creative</div>
      <div className="text-[26px] md:text-[44px] font-extrabold uppercase text-white leading-tight drop-shadow-[0_3px_0_rgba(0,0,0,0.7)]">Private Investor Agreement</div>
    </div>
  </div>
);

export const ErrorBanner = ({ count, onSee }) => {
  if (!count) return null;
  return (
    <div className="fixed top-0 inset-x-0 z-50 bg-[#DC2626] text-white min-h-[48px] flex flex-wrap items-center justify-center gap-2 md:gap-4 px-3 py-2 text-[13px] md:text-[15px] text-center" role="alert" aria-live="assertive" data-testid="onboarding-error-banner">
      <span>There {count === 1 ? 'is' : 'are'} <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white text-[#DC2626] text-[12px] font-bold mx-1">{count}</span> error{count === 1 ? '' : 's'} on this page. Please correct {count === 1 ? 'it' : 'them'} before moving on.</span>
      <button onClick={onSee} data-testid="onboarding-see-errors" className="px-4 md:px-5 py-1.5 md:py-2 bg-white text-[#DC2626] text-[13px] md:text-[14px] font-medium rounded-sm">See Errors</button>
    </div>
  );
};

export const FooterBar = () => (
  <div className="bg-[#0A1551] min-h-[46px] flex items-center px-4 py-2 text-white" data-testid="onboarding-footer">
    <Link to="/login" className="flex items-center gap-2"><BrandMark size={26} light /><span className="font-bold text-[17px] md:text-[20px] tracking-tight">{SIGNED_IN_PRODUCT_NAME}</span></Link>
    <div className="ml-auto flex items-center gap-4">
      <span className="hidden md:inline text-[12px]">Already a member of {PUBLIC_SITE_NAME}? Log in to {SIGNED_IN_PRODUCT_NAME}</span>
      <Link to="/login" data-testid="footer-login-link" className="px-3 md:px-4 py-1.5 bg-[#78BB07] hover:bg-[#6AA806] text-white text-[12px] md:text-[13px] font-medium rounded-sm whitespace-nowrap">Log in</Link>
    </div>
  </div>
);

export const PageShell = ({ children }) => (
  <div className="min-h-screen flex flex-col text-white relative overflow-x-hidden" style={{ fontFamily: 'Cabin, sans-serif', backgroundColor: '#0B1440', backgroundImage: `url(${BG_IMAGE_URL})`, backgroundSize: 'cover', backgroundPosition: 'center top', backgroundAttachment: 'fixed' }} data-testid="landing-page">
    <div className="pointer-events-none absolute inset-0" aria-hidden="true" style={{ background: 'linear-gradient(90deg, rgba(8,17,52,0.92) 0%, rgba(8,17,52,0.72) 45%, rgba(8,17,52,0.45) 100%)' }} />
    {BG_LOGO_URL && (
      <div className="pointer-events-none absolute top-[150px] md:top-[120px] left-1/2 -translate-x-1/2 md:left-auto md:right-[12%] md:translate-x-0 w-[min(72vw,560px)]" aria-hidden="true">
        <img src={BG_LOGO_URL} alt="" className="w-full opacity-[0.11] blur-[0.5px]" data-testid="brand-bg-logo" />
      </div>
    )}
    <div className="flex-1 relative">
      <div className="max-w-[1252px] mx-auto px-4 md:px-[42px]">
        <Banner />
        {children}
      </div>
    </div>
    <FooterBar />
  </div>
);

export const Heading = ({ children }) => <h2 className="text-[22px] font-medium mb-3" style={{ color: MAGENTA }}>{children}</h2>;
export const P = ({ children, className = '' }) => <p className={`text-[16px] leading-[1.45] mb-3 ${className}`}>{children}</p>;

export const NavButtons = ({ onBack, onNext, nextLabel = 'Next', hasErrors, submitting }) => (
  <div className="border-t border-white/70 mt-6 pt-4 pb-8 flex flex-wrap items-center justify-between gap-3">
    <div>{onBack && <button type="button" onClick={onBack} data-testid="onboarding-back" className="px-8 md:px-10 py-2.5 text-[16px] md:text-[17px] text-white rounded-[4px]" style={{ backgroundColor: TEAL_BTN }}>Back</button>}</div>
    <div className="flex items-center gap-3">
      {hasErrors && <span className="hidden sm:inline text-[13px] text-white">There are errors on this page. Please fix them before continuing.</span>}
      <button type="button" onClick={onNext} disabled={submitting} data-testid={nextLabel === 'Submit' ? 'onboarding-submit' : 'onboarding-next'} className="px-8 md:px-10 py-2.5 text-[16px] md:text-[17px] text-white rounded-[4px] disabled:opacity-60" style={{ backgroundColor: TEAL_BTN }}>{submitting ? 'Submitting…' : nextLabel}</button>
    </div>
  </div>
);
